
import { supabase } from "@/integrations/supabase/client";

export const getApplicableDiscount = async (schoolId: string, productId: string | undefined, planId: string) => {
  if (!schoolId || !planId) {
    console.log('Missing required parameters for discount check:', { schoolId, planId });
    return null;
  }

  try {
    console.log(`Checking discount for: school=${schoolId}, product=${productId}, plan=${planId}`);
    
    // First, try to find a plan-specific discount that matches the exact plan_id
    let { data: planDiscount, error: planError } = await supabase
      .from('discounts')
      .select('*')
      .eq('school_id', schoolId)
      .eq('plan_id', planId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);

    if (planError) {
      console.error('Error fetching plan discount:', planError);
      return null;
    }

    // If a plan-specific discount was found, return it
    if (planDiscount && planDiscount.length > 0) {
      console.log('Found plan-specific discount:', planDiscount[0]);
      return planDiscount[0];
    }

    // Get the plan details to check plan type (yearly, monthly, etc.)
    const { data: planDetails, error: planDetailsError } = await supabase
      .from('plans')
      .select('id, name, feature_id, type')
      .eq('id', planId)
      .single();
      
    if (planDetailsError) {
      console.error('Error fetching plan details:', planDetailsError);
      return null;
    }
    
    if (!planDetails) {
      console.log(`No plan details found for plan ID ${planId}`);
      return null;
    }

    // If no plan-specific discount was found AND we have a productId, check for product-level discounts
    if (productId) {
      console.log(`Checking for product-specific discount for product ${productId}`);
      
      // First, check for product discount that applies to this specific plan type
      const planType = planDetails.type; // e.g., "Monthly", "Yearly", "One-Time"
      
      let { data: productTypeDiscount, error: productTypeError } = await supabase
        .from('discounts')
        .select('*')
        .eq('school_id', schoolId)
        .eq('product_id', productId)
        .eq('status', 'active')
        .ilike('description', `%${planType}%`) // Check if description mentions this plan type
        .order('created_at', { ascending: false })
        .limit(1);

      if (productTypeError) {
        console.error('Error fetching product+type discount:', productTypeError);
      } else if (productTypeDiscount && productTypeDiscount.length > 0) {
        console.log(`Found type-specific product discount for ${planType}:`, productTypeDiscount[0]);
        return productTypeDiscount[0];
      }
      
      // If no type-specific discount found, check for generic product discount
      let { data: productDiscount, error: productError } = await supabase
        .from('discounts')
        .select('*')
        .eq('school_id', schoolId)
        .eq('product_id', productId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (productError) {
        console.error('Error fetching product discount:', productError);
      } else if (productDiscount && productDiscount.length > 0) {
        console.log('Found generic product discount:', productDiscount[0]);
        return productDiscount[0];
      }
    }

    // If no plan or product discount found, check for school-level discount
    let { data: schoolDiscount, error: schoolError } = await supabase
      .from('discounts')
      .select('*')
      .eq('school_id', schoolId)
      .is('plan_id', null)
      .is('product_id', null)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);

    if (schoolError) {
      console.error('Error fetching school discount:', schoolError);
      return null;
    }

    // If a school-level discount was found, return it
    if (schoolDiscount && schoolDiscount.length > 0) {
      console.log('Found school-level discount:', schoolDiscount[0]);
      return schoolDiscount[0];
    }

    // If no discount was found at any level, return null
    console.log('No applicable discount found for plan:', planId);
    return null;
  } catch (error) {
    console.error('Error in getApplicableDiscount:', error);
    return null;
  }
};
